/**
 * LanguageProvider - Multi-language support for Innolab Trader MFEs
 *
 * Supports 8 languages: EN, ES, FR, DE, PT, ZH, JA, RU
 * 🇺🇸 🇪🇸 🇫🇷 🇩🇪 🇧🇷 🇨🇳 🇯🇵 🇷🇺
 */

import React, { createContext, useContext, useEffect, useState } from 'react';
import { loadLocale } from './loader';
import { AVAILABLE_LANGUAGES, type Language } from './types';

interface LanguageContextType {
  language: Language;
  setLanguage: (lang: Language) => void;
  t: (key: string) => string;
  messages: Partial<Record<Language, any>>;
  availableLanguages: typeof AVAILABLE_LANGUAGES;
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

const STORAGE_KEY = 'innolab-trader-language';

interface LanguageProviderProps {
  children: React.ReactNode;
  defaultLanguage?: Language;
  storageKey?: string;
}

export function LanguageProvider({
  children,
  defaultLanguage = 'en',
  storageKey = STORAGE_KEY
}: LanguageProviderProps) {
  const [language, setLanguageState] = useState<Language>(() => {
    // Determine initial language from browser settings if available
    if (typeof navigator !== 'undefined') {
      const browserLang = navigator.language?.toLowerCase?.() || ''
      const match = AVAILABLE_LANGUAGES.find(l => browserLang.startsWith(l.code) || browserLang === l.code)
      if (match) return match.code
    }
    return defaultLanguage
  })

  // Loaded message catalogs (current + English fallback)
  const [messages, setMessages] = useState<Record<Language, any>>({} as any);

  // Ensure English fallback is loaded once
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const en = await loadLocale('en');
      if (!cancelled) {
        setMessages((prev) => ({ ...prev, en }));
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // Load current language catalog on change
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const cur = await loadLocale(language);
      if (!cancelled) {
        setMessages((prev) => ({ ...prev, [language]: cur }));
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [language]);

  // Persist language choice
  useEffect(() => {
    document.documentElement.lang = language
  }, [language, storageKey])

  const setLanguage = (lang: Language) => {
    setLanguageState(lang);
  };

  // Translation function with support for dot-notation keys
  const t = (key: string): string => {
    const getNested = (obj: any, path: string): string | undefined => {
      const parts = path.split('.');
      let cur: any = obj;
      for (const p of parts) {
        if (cur && typeof cur === 'object' && p in cur) cur = cur[p];
        else return undefined;
      }
      return typeof cur === 'string' ? cur : undefined;
    };

    // Try current language (if loaded)
    const nestedCurr = messages[language] ? getNested(messages[language], key) : undefined;
    if (nestedCurr) return nestedCurr;

    // Fallback to English (if loaded)
    const nestedEn = messages.en ? getNested(messages.en, key) : undefined;
    if (nestedEn) return nestedEn;

    console.warn(`Translation missing for key: ${key}`);
    return key;
  };

  const value = {
    language,
    setLanguage,
    t,
    messages,
    availableLanguages: AVAILABLE_LANGUAGES
  };

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
}

// Hook to use language context
export function useLanguage() {
  const context = useContext(LanguageContext);
  if (context === undefined) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
}

export default LanguageProvider;
